import { useMemo, useState } from 'react';
import * as SmartySDK from 'smartystreets-javascript-sdk';
import { SMARTY_AUTOCOMPLETE } from '../config';

const SmartyCore = SmartySDK.core;

export default function useAddressAutoComplete(): {
  queryAutocompleteForSuggestions: (
    query: string,
    secondary?: SmartySDK.usAutocompletePro.Suggestion
  ) => void;
  queryStreetValidation: (
    query: string
  ) => Promise<SmartySDK.usStreet.Candidate | null | void>;
  suggestions: SmartySDK.usAutocompletePro.Suggestion[];
  suggestedAddress: SmartySDK.usStreet.Candidate | null;
  clearSuggestions: () => void;
  clearSuggestedAddress: () => void;
} {
  const [suggestions, setSuggestions] = useState<
    SmartySDK.usAutocompletePro.Suggestion[]
  >([]);
  const [suggestedAddress, setSuggestedAddress] =
    useState<SmartySDK.usStreet.Candidate | null>(
      {} as SmartySDK.usStreet.Candidate
    );
  const smartySharedCredentials = useMemo(
    () => new SmartyCore.SharedCredentials(SMARTY_AUTOCOMPLETE),
    []
  );
  const autoCompleteClient = useMemo(
    () =>
      new SmartyCore.ClientBuilder(
        smartySharedCredentials
      ).buildUsAutocompleteProClient(),
    [smartySharedCredentials]
  );

  const usStreetClient = useMemo(
    () =>
      new SmartyCore.ClientBuilder(
        smartySharedCredentials
      ).buildUsStreetApiClient(),
    [smartySharedCredentials]
  );

  const queryAutocompleteForSuggestions = (
    query: string,
    secondary?: SmartySDK.usAutocompletePro.Suggestion
  ): void => {
    const lookup = new SmartySDK.usAutocompletePro.Lookup(query);

    if (secondary) {
      lookup.selected = `${secondary.streetLine} ${secondary.secondary} ${
        secondary.entries > 0 ? `(${secondary.entries}) ` : ''
      }${secondary.city} ${secondary.state} ${secondary.zipcode}`;
      lookup.maxResults = 10;
    }
    if (query.length > 0) {
      autoCompleteClient
        .send(lookup)
        .then((results) => {
          setSuggestions(results.result);
        })
        .catch(console.warn);
    }
  };

  const queryStreetValidation = (
    query: string
  ): Promise<void | SmartySDK.usStreet.Candidate | null> => {
    const [street1, street2, city, state, zip] = query.split(',');
    const lookup = new SmartySDK.usStreet.Lookup();
    lookup.street = street1.trim();
    lookup.street2 = street2.trim();
    lookup.city = city.trim();
    lookup.state = state.trim();
    lookup.zipCode = zip.trim();

    return usStreetClient
      .send(lookup)
      .then((results) => {
        if (results.lookups[0].result.length > 0) {
          setSuggestedAddress(results.lookups[0].result[0]);
          return results.lookups[0].result[0];
        }
        setSuggestedAddress(null);
        return null;
      })
      .catch(console.warn);
  };

  const clearSuggestions = () => setSuggestions([]);

  const clearSuggestedAddress = () => setSuggestedAddress(null);

  return {
    queryAutocompleteForSuggestions,
    queryStreetValidation,
    suggestions,
    suggestedAddress,
    clearSuggestions,
    clearSuggestedAddress,
  };
}
